import { computed, ref, type ComputedRef } from 'vue';

export type InstallPlatform = 'android' | 'ios' | 'unsupported';

export const DISMISSAL_STORAGE_KEY = 'votewise:install-prompt-dismissed-at';
export const DISMISSAL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * The non-standard event Chromium fires when a site is installable. Not in
 * lib.dom, so it is declared locally rather than widened to `any`.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Where the inline `<head>` script in `nuxt.config.ts` parks an event that
 * arrived before this bundle existed, plus the feature-detected
 * `getInstalledRelatedApps` — neither is in lib.dom.
 */
interface InstallGlobals {
  __votewiseInstallPrompt?: BeforeInstallPromptEvent | null;
  navigator: Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string }>>;
  };
}

// Module scope, not per-component: `beforeinstallprompt` fires once, during
// initial page load, typically before any component has mounted. A listener
// registered in `onMounted` would miss it entirely and the Android path would
// never light up.
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
// Only the homepage mounts the banner, but the space for it is reserved in the
// shared layout — so the layout has to know the banner is actually there, not
// merely that it *could* show, or every other route gains 88px of dead space.
const bannerMounted = ref(false);
let listenersBound = false;
const installed = ref(false);
const dismissedAt = ref<number | null>(null);

function isStandalone(): boolean {
  if (!import.meta.client) return false;

  // `display-mode: standalone` covers Android and installed-PWA Safari;
  // `navigator.standalone` is the legacy iOS-only signal. Both are only ever
  // true *inside* the installed app, never in the browser tab showing the
  // banner — which is why an iOS install cannot be detected from the tab.
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    nav.standalone === true
  );
}

function isIos(): boolean {
  if (!import.meta.client) return false;

  const nav = window.navigator as Navigator & { maxTouchPoints?: number };
  // iPadOS 13+ reports a desktop UA, so it is identified by the Mac platform
  // string combined with a touch screen.
  return (
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1)
  );
}

function readDismissedAt(): number | null {
  if (!import.meta.client) return null;
  try {
    const raw = localStorage.getItem(DISMISSAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Storage disabled (private mode, blocked cookies). Treat as "never
    // dismissed" rather than failing — worst case the user sees the banner
    // again, which is better than an error on the homepage.
    return null;
  }
}

/**
 * Asks the browser whether our own PWA is already installed.
 *
 * A browser tab cannot otherwise tell: `display-mode: standalone` is only true
 * inside the installed app, so an installed user looks identical to a browser
 * that never fired `beforeinstallprompt` — and Chromium suppresses that event
 * entirely once the app is installed. Without this, an installed device shows
 * no banner and no reason for it, which is indistinguishable from the bug.
 *
 * Requires `related_applications` in the manifest (see `nuxt.config.ts`); a
 * no-op wherever the API or that entry is absent.
 *
 * Scope, so nobody over-reads this: the API is Chromium-only, so on Android it
 * mostly makes an already-true state explicit — an installed app there already
 * suppresses `beforeinstallprompt`, which alone hides the banner. It does NOT
 * fix the installed-iOS case: Safari has no such API, so an installed iOS user
 * browsing in a tab still sees the banner (`platform` is user-agent based and
 * `isStandalone()` is false outside the installed app). That gap needs a
 * different answer — a deliberate dismissal, or accepting it — not this probe.
 */
async function detectInstalledRelatedApp(): Promise<void> {
  const nav = (window as unknown as InstallGlobals).navigator;
  if (typeof nav.getInstalledRelatedApps !== 'function') return;

  try {
    const apps = await nav.getInstalledRelatedApps();
    if (!apps.some((app) => app.platform === 'webapp')) return;

    installed.value = true;
    deferredPrompt.value = null;
  } catch {
    // Not supported, or refused in this context. Leave the state alone: a
    // failed probe must not be read as "installed".
  }
}

function recordDismissal(): void {
  const now = Date.now();
  dismissedAt.value = now;
  if (!import.meta.client) return;
  try {
    localStorage.setItem(DISMISSAL_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable — the in-memory value still suppresses the banner
    // for this session, which satisfies "hides for the session".
  }
}

/**
 * Binds the install listeners and seeds the initial state. Idempotent: repeat
 * calls are no-ops, so the module-evaluation call below and the boot-time call
 * from `~/plugins/installPrompt.client` cannot double-bind.
 *
 * The plugin is what makes this reliable — `beforeinstallprompt` is one-shot
 * and fires during initial load, so binding only when the banner component's
 * chunk loads would miss it on a client-side navigation into the homepage.
 */
export function initInstallPrompt(): void {
  if (!import.meta.client || listenersBound) return;
  listenersBound = true;

  installed.value = isStandalone();
  dismissedAt.value = readDismissedAt();

  // VTW-542: the event may already have fired. `beforeinstallprompt` is
  // one-shot and Chromium dispatches it as soon as the page is installable,
  // which on a warm cache is before this bundle has run — so binding a listener
  // is not enough on its own. The inline head script catches that case and
  // parks the event here; a missed event was the whole reason the Android
  // banner appeared once and then never again.
  const early = (window as unknown as InstallGlobals).__votewiseInstallPrompt;
  if (early) {
    deferredPrompt.value = early;
    (window as unknown as InstallGlobals).__votewiseInstallPrompt = null;
  }

  void detectInstalledRelatedApp();

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chromium's own mini-infobar so our banner is the only prompt.
    event.preventDefault();
    deferredPrompt.value = event as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    installed.value = true;
    deferredPrompt.value = null;
  });
}

initInstallPrompt();

export function useInstallPrompt() {
  const isInstalled: ComputedRef<boolean> = computed(() => installed.value);

  const platform: ComputedRef<InstallPlatform> = computed(() => {
    if (isIos()) return 'ios';
    // Android is gated on the captured event, not the user agent: a browser
    // that cannot install never fires it, and must not be shown an Install
    // button that would do nothing.
    if (deferredPrompt.value) return 'android';
    return 'unsupported';
  });

  const isDismissalActive = computed(() => {
    if (dismissedAt.value === null) return false;
    // `Date.now()` is not reactive, so this only re-evaluates when `dismissedAt`
    // changes — not as wall-clock time crosses the 3-day boundary. A tab left
    // open across the expiry keeps the banner hidden until a reload or some
    // other recompute. Deliberate: next-navigation granularity is right for a
    // banner, and a reload re-reads the timestamp from storage anyway.
    return Date.now() - dismissedAt.value < DISMISSAL_WINDOW_MS;
  });

  const canShowBanner = computed(
    () =>
      !isInstalled.value &&
      platform.value !== 'unsupported' &&
      !isDismissalActive.value,
  );

  // What the layout reserves space against: the banner is on screen only where
  // it is mounted *and* allowed to show.
  const isBannerOnScreen = computed(
    () => bannerMounted.value && canShowBanner.value,
  );

  /** Called by the banner itself so the layout can track its presence. */
  function setBannerMounted(mounted: boolean): void {
    bannerMounted.value = mounted;
  }

  async function promptInstall(): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > {
    const event = deferredPrompt.value;
    if (!event) return 'unavailable';

    // Claim the event before the first `await` so a second, overlapping call
    // (e.g. a fast double-tap) sees it already cleared and bails out above,
    // rather than both calls racing to invoke the single-use `prompt()`.
    deferredPrompt.value = null;

    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      // Declining the native prompt is a dismissal in substance. Without
      // recording one the banner would vanish anyway (the single-use event is
      // spent, so `platform` falls back to 'unsupported') and then reappear on
      // the next full load, having never honoured the user's "no".
      if (outcome === 'dismissed') recordDismissal();
      return outcome;
    } catch {
      // Chromium rejects `prompt()` if it is called more than once on the
      // same event; treat that as "no prompt available" rather than letting
      // it surface as an unhandled rejection.
      return 'unavailable';
    }
  }

  return {
    platform,
    isInstalled,
    canShowBanner,
    isBannerOnScreen,
    setBannerMounted,
    promptInstall,
    dismiss: recordDismissal,
  };
}
